import ConfigTabTemplate from './ConfigTabTemplate';
import { Controller, useFormContext } from 'react-hook-form';
import { Stack } from '@mui/material';
import FormSelect from '../molecules/FormSelect';
import FormFieldStack from '../atoms/FormFieldStack';
import Switch from '../atoms/Switch';
import CollapsibleSection from '../atoms/CollapsibleSection';
import { SportsbookConfigFormData, sportsbookConfigSchema } from '../../schema';
import {
  ODDS_FORMAT_OPTIONS,
  REOFFER_ODDS_DISPLAY_OPTIONS,
  TEAM_ASSETS_VARIANT_OPTIONS,
  SPORT_ICON_VARIANT_OPTIONS,
  COMPETITIONS_FLAG_VARIANT_OPTIONS,
} from '../../constants';
import { useFeature } from '@growthbook/growthbook-react';
import { FEATURE_FLAG } from '../../constants/featureFlags';
import { SelectOption } from '../../@types';
import {
  REOFFER_ODDS_DISPLAY,
  ODDS_FORMAT,
  FEED_SOURCE,
  TEAM_ASSETS_VARIANT,
  SPORT_ICON_VARIANT,
  COMPETITIONS_FLAG_VARIANT,
} from '../../constants/enums';

const DEFAULT_VALUES = {
  oddsFormat: ODDS_FORMAT.DECIMAL,
  reofferOddsDisplay: REOFFER_ODDS_DISPLAY.ALL,
  feedSource: FEED_SOURCE.FEED_THE_BET,
  ftbTicketValidation: false,
  assetVariants: {
    sportIcons: SPORT_ICON_VARIANT.MULTICOLOR,
    competitionsFlags: COMPETITIONS_FLAG_VARIANT.DEFAULT,
    teamAssets: TEAM_ASSETS_VARIANT.NONE,
  },
  AdditionalFeatures: { BonusesEngine: false, JackpotEngine: false },
  widgetFeatures: { preMatchWidget: false, liveWidget: false },
  betBuilder: false,
} satisfies SportsbookConfigFormData;

const resolveSportsbookConfigInitialValues = (data?: Partial<SportsbookConfigFormData>): SportsbookConfigFormData => {
  return {
    ...DEFAULT_VALUES,
    ...data,
    assetVariants: {
      ...DEFAULT_VALUES.assetVariants,
      ...data?.assetVariants,
    },
    AdditionalFeatures: {
      ...DEFAULT_VALUES.AdditionalFeatures,
      ...data?.AdditionalFeatures,
    },
    widgetFeatures: {
      ...DEFAULT_VALUES.widgetFeatures,
      ...data?.widgetFeatures,
    },
    betBuilder: data?.betBuilder ?? DEFAULT_VALUES.betBuilder,
  };
};

const MarketplaceSportsbookConfigFields = () => {
  const { value: feedSources } = useFeature<SelectOption[]>(FEATURE_FLAG.FEED_SOURCE_OPTIONS);

  const {
    control,
    formState: { errors },
  } = useFormContext<SportsbookConfigFormData>();

  return (
    <Stack spacing={2}>
      <FormSelect
        label='Odds Format'
        name='oddsFormat'
        control={control}
        error={errors.oddsFormat}
        options={ODDS_FORMAT_OPTIONS}
        required
      />
      <FormSelect
        label='Reoffer Odds Display'
        name='reofferOddsDisplay'
        control={control}
        error={errors.reofferOddsDisplay}
        options={REOFFER_ODDS_DISPLAY_OPTIONS}
        allowDeselect={false}
        required
      />

      <FormSelect
        label='Feed Source'
        name='feedSource'
        control={control}
        error={errors.feedSource}
        options={feedSources ?? []}
        disabled
      />
      <CollapsibleSection title='Asset Variants'>
        <Stack spacing={2}>
          <FormSelect
            label='Sport Icons'
            name='assetVariants.sportIcons'
            control={control}
            error={errors.assetVariants?.sportIcons}
            options={SPORT_ICON_VARIANT_OPTIONS}
            allowDeselect={false}
            required
          />
          <FormSelect
            label='Competition Flags'
            name='assetVariants.competitionsFlags'
            control={control}
            error={errors.assetVariants?.competitionsFlags}
            options={COMPETITIONS_FLAG_VARIANT_OPTIONS}
            allowDeselect={false}
            required
          />
          <FormSelect
            label='Team Assets'
            name='assetVariants.teamAssets'
            control={control}
            error={errors.assetVariants?.teamAssets}
            options={TEAM_ASSETS_VARIANT_OPTIONS}
            allowDeselect={false}
            required
          />
        </Stack>
      </CollapsibleSection>
      <FormFieldStack label='FTB Ticket Validation'>
        <Controller
          name='ftbTicketValidation'
          control={control}
          render={({ field }) => <Switch {...field} ref={null} />}
        />
      </FormFieldStack>
      <FormFieldStack label='Bonuses Engine'>
        <Controller
          name='AdditionalFeatures.BonusesEngine'
          control={control}
          render={({ field }) => <Switch {...field} ref={null} />}
        />
      </FormFieldStack>
      <FormFieldStack label='Jackpot Engine'>
        <Controller
          name='AdditionalFeatures.JackpotEngine'
          control={control}
          render={({ field }) => <Switch {...field} ref={null} />}
        />
      </FormFieldStack>
      <FormFieldStack label='Pre-Match Widget'>
        <Controller
          name='widgetFeatures.preMatchWidget'
          control={control}
          render={({ field }) => <Switch {...field} ref={null} />}
        />
      </FormFieldStack>
      <FormFieldStack label='Live Widget'>
        <Controller
          name='widgetFeatures.liveWidget'
          control={control}
          render={({ field }) => <Switch {...field} ref={null} />}
        />
      </FormFieldStack>
      <FormFieldStack label='Bet Builder'>
        <Controller name='betBuilder' control={control} render={({ field }) => <Switch {...field} ref={null} />} />
      </FormFieldStack>
    </Stack>
  );
};

const MarketplaceSportsbookConfigTab = () => {
  const excludeFeedSource = (data: SportsbookConfigFormData) => {
    const { feedSource, ...restData } = data;

    return restData;
  };

  return (
    <ConfigTabTemplate<SportsbookConfigFormData>
      dataKey='sportsbookConfig'
      schema={sportsbookConfigSchema}
      defaultValues={DEFAULT_VALUES}
      transformData={excludeFeedSource}
      resolveInitialValues={resolveSportsbookConfigInitialValues}
    >
      <MarketplaceSportsbookConfigFields />
    </ConfigTabTemplate>
  );
};

export default MarketplaceSportsbookConfigTab;
